import { DimensionMap } from "models/propTypes/dimensionPropTypes";
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
import LocalizationHelperInstance from "./localizationHelper";
import { AppState } from "store";
import { DimensionEnums } from "models/enums";
import ArrayHelper from "./arrayHelper";
import { MissingDocumentFormTypes } from "models/formTypes";

const DimensionHelper = {
  getValidList: (receivedDimensions: WintSuperkollAPIModel.DimensionData[], dimensionMap: DimensionMap) => {
    const allValidDimensions = dimensionMap.flatMap((dm) => dm.value.map((d) => d.id));
    return receivedDimensions.filter((d) => allValidDimensions.some((vd) => d.dimensionId === vd));
  },
  getDimensionTypeText: (dimensionType: WintSuperkollAPIModel.DimensionCompatibilityType, name?: string) => {
    return dimensionType === 0
      ? (name ?? "")
      : LocalizationHelperInstance.getFormattedMessage(`Common.DimensionCompatibilityType_${dimensionType}`, name);
  },
  toCompatibilityType: (
    dimensionTypes?: WintSuperkollAPIModel.DimensionTypes
  ): WintSuperkollAPIModel.DimensionCompatibilityType => {
    switch (dimensionTypes) {
      case WintSuperkollAPIModel.DimensionTypes.CostCenter:
        return WintSuperkollAPIModel.DimensionCompatibilityType.CostCenter;
      case WintSuperkollAPIModel.DimensionTypes.Project:
        return WintSuperkollAPIModel.DimensionCompatibilityType.Project;
      default:
        return WintSuperkollAPIModel.DimensionCompatibilityType.Default;
    }
  },
  getTranslatedDimensionTypeText: (
    dimensionType: WintSuperkollAPIModel.DimensionCompatibilityType,
    name?: string,
    compatibilityType?: WintSuperkollAPIModel.DimensionTypes
  ) => {
    const resolved =
      dimensionType === WintSuperkollAPIModel.DimensionCompatibilityType.Default
        ? DimensionHelper.toCompatibilityType(compatibilityType)
        : dimensionType;

    return DimensionHelper.getDimensionTypeText(resolved, name);
  },
  getAppStateDimensionSelector: (type: DimensionEnums.DimensionCategory) => {
    return (store: AppState) => {
      switch (type) {
        case DimensionEnums.DimensionCategory.IncomingInvoice:
          return {
            dimensionTypes: store.auth.selectedCompany.incomingInvoiceDimensionTypes ?? [],
            dimensions: store.common.dimensions.incomingInvoiceDimensions ?? [],
            dimensionSuggestions: store.common.dimensions.incomingInvoiceDimensionSuggestions ?? [],
          };
        case DimensionEnums.DimensionCategory.Invoice:
          return {
            dimensionTypes: store.auth.selectedCompany.invoiceDimensionTypes ?? [],
            dimensions: store.common.dimensions.invoiceDimensions ?? [],
            dimensionSuggestions: store.common.dimensions.invoiceDimensionSuggestions ?? [],
          };
        case DimensionEnums.DimensionCategory.Receipt:
          return {
            dimensionTypes: store.auth.selectedCompany.receiptDimensionTypes ?? [],
            dimensions: store.common.dimensions.receiptDimensions ?? [],
            dimensionSuggestions: store.common.dimensions.receiptDimensionSuggestions ?? [],
          };
        case DimensionEnums.DimensionCategory.CashReconciliation:
          return {
            dimensionTypes: (store.auth.selectedCompany.cashReconciliationDimensionTypes ?? []).sort(
              (a, b) => (a.compatibilityType ?? 0) - (b.compatibilityType ?? 0)
            ),
            dimensions: store.common.dimensions.cashReconciliationDimensions ?? [],
            dimensionSuggestions: store.common.dimensions.allDimensions ?? [],
          };
        case DimensionEnums.DimensionCategory.All:
          return {
            dimensionTypes: store.common.dimensions.allDimensionTypes ?? [],
            dimensions: store.common.dimensions.allDimensions ?? [],
            dimensionSuggestions: store.common.dimensions.allDimensions ?? [],
          };
        case DimensionEnums.DimensionCategory.MissingDocumentOther:
          // missing document dimensions are a combination of active dimensions from incoming invoices and receipts
          const missingDocumentDimensions = ArrayHelper.uniqueBy(
            (store.common.dimensions.incomingInvoiceDimensions ?? []).concat(
              store.common.dimensions.receiptDimensions ?? []
            ),
            (item) => item.key.id
          );

          return {
            dimensionTypes: missingDocumentDimensions.map((dim) => dim.key),
            dimensions: missingDocumentDimensions,
            dimensionSuggestions: missingDocumentDimensions,
          };
      }
    };
  },
  getDimensionListFromMissingDocumentTodoForm: (
    value: MissingDocumentFormTypes.Todo
  ): WintSuperkollAPIModel.DimensionData[] => {
    if (value.type === WintSuperkollAPIModel.MissingDocumentPutType.Receipt) {
      return value.receipt?.dimensions ?? [];
    } else if (value.type === WintSuperkollAPIModel.MissingDocumentPutType.IncomingInvoice) {
      return value.incomingInvoice?.dimensions ?? [];
    } else if (value.type === WintSuperkollAPIModel.MissingDocumentPutType.Other) {
      return value.other?.dimensions ?? [];
    } else {
      return [];
    }
  },
};

export default DimensionHelper;
